import { memo } from 'react'
import { StyleSheet, View } from 'react-native'
import { Text } from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'
import { formatCompactDuration } from '@/lib/map-utils'
import type { OnSiteClusterData } from '@/types/mapData'
import { Callout, Marker } from '@/ui/MapView'

interface Props {
    cluster: OnSiteClusterData
}

export const OnSiteClusterBadge = memo(function OnSiteClusterBadge({ cluster }: Props) {
    const semantic = useSemanticColors()
    const count = cluster.members.length
    const visibleDots = cluster.members.slice(0, 4)
    const extra = count - visibleDots.length

    return (
        <Marker coordinate={cluster.center} anchor={{ x: 0.5, y: 0.5 }} tracksViewChanges={true}>
            <View
                style={[styles.badge, { backgroundColor: semantic.success, borderColor: '#fff' }]}
            >
                <Text style={styles.count}>{count}</Text>
                <View style={styles.dotsRow}>
                    {visibleDots.map(m => (
                        <View
                            key={m.userId}
                            style={[styles.dot, { backgroundColor: m.userColor }]}
                        />
                    ))}
                    {extra > 0 ? <Text style={styles.extra}>+{extra}</Text> : null}
                </View>
            </View>
            <Callout>
                <View style={styles.callout}>
                    <Text style={styles.calloutTitle}>{cluster.siteJobName}</Text>
                    {cluster.members.map(m => (
                        <View key={m.userId} style={styles.calloutRow}>
                            <View style={[styles.calloutDot, { backgroundColor: m.userColor }]} />
                            <Text style={styles.calloutName}>{m.userName}</Text>
                            {m.stationaryDurationMinutes != null ? (
                                <Text style={styles.calloutDur}>
                                    {formatCompactDuration(m.stationaryDurationMinutes * 60)}
                                </Text>
                            ) : null}
                        </View>
                    ))}
                </View>
            </Callout>
        </Marker>
    )
})

const styles = StyleSheet.create({
    badge: {
        minWidth: 54,
        paddingHorizontal: 8,
        paddingVertical: 6,
        borderRadius: 20,
        borderWidth: 2,
        alignItems: 'center',
    },
    count: {
        color: '#fff',
        fontWeight: '700',
        fontSize: 14,
    },
    dotsRow: {
        flexDirection: 'row',
        gap: 2,
        marginTop: 2,
        alignItems: 'center',
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: 4,
    },
    extra: {
        color: '#fff',
        fontSize: 10,
        marginLeft: 2,
    },
    callout: {
        minWidth: 180,
        padding: 6,
    },
    calloutTitle: {
        fontWeight: '700',
        marginBottom: 4,
    },
    calloutRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 2,
    },
    calloutDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
    },
    calloutName: {
        flex: 1,
        fontSize: 13,
    },
    calloutDur: {
        fontSize: 12,
        color: '#666',
    },
})
